import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import pick from 'lodash/pick'
import {
    Grid,
} from '@mui/material'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import StatusHandler from 'app/shared-components/StatusHandler'
import FormWithControls from 'app/shared-components/FormWithControls'
import {
    ContainerStatus,
} from 'app/enums/label.enums'

import containerRequests from 'app/requests/Container/Container.requests'
import {
    WithId,
} from 'app/types/common.types'
import useGetContainerBySerialNumber from '../../hooks/useGetContainerBySerialNumber'

import useGetAllowedContainerStatusTransitions from './hooks/useGetAllowedContainerStatusTransitions'
import changeStatusFields from './ChangeStatus.fields'
import useStyles from './ChangeStatus.styles'
import {
    ContainerChangeStatus,
} from './ChangeStatus.types'

export default function ChangeStatus({
    id: serialNumber,
}: Readonly<WithId<string>>) {
    const {
        classes,
    } = useStyles()

    const {
        data,
        isDataReady,
        isError,
        error,
        isFetching,
        invalidate,
    } = useGetContainerBySerialNumber(serialNumber)

    const statusTransitions = useGetAllowedContainerStatusTransitions({
        serialNumber: data?.serialNumber,
        currentStatus: data?.containerStatus,
    })

    const getAllowedValues = useEnumValues()

    const [
        isToBeLoggerExchanged,
        setIsToBeLoggerExchanged,
    ] = useState(false)

    const checkIfToBeLoggerExchanged = useCallback((containerStatus: ContainerStatus): boolean => {
        return containerStatus === ContainerStatus.TO_BE_LOGGER_EXCHANGED
    }, [])

    const onChange = useCallback(({
        containerStatus: newContainerStatus,
    }: ContainerChangeStatus) => {
        setIsToBeLoggerExchanged(checkIfToBeLoggerExchanged(newContainerStatus))
    }, [checkIfToBeLoggerExchanged])

    useEffect(() => {
        if (isDataReady) {
            setIsToBeLoggerExchanged(checkIfToBeLoggerExchanged(data?.containerStatus))
        }
    }, [
        checkIfToBeLoggerExchanged,
        data?.containerStatus,
        isDataReady,
    ])

    const fieldsWithClasses = useFieldsWithClassName(
        changeStatusFields({
            isToBeLoggerExchanged, getAllowedValues, statusTransitions,
        }),
        classes,
    )

    const value: ContainerChangeStatus = useMemo(() => {
        return pick(data, changeStatusFields({
            isToBeLoggerExchanged: checkIfToBeLoggerExchanged(data?.containerStatus),
            getAllowedValues,
            statusTransitions,
        })[0].fields.map(({
            name,
        }) => {
            return name
        }))
    }, [
        checkIfToBeLoggerExchanged,
        data,
        getAllowedValues,
        statusTransitions,
    ])

    const modifyDataBeforeSend = useCallback((formValue: ContainerChangeStatus) => {
        return {
            ...formValue,
            ...(!checkIfToBeLoggerExchanged(formValue.containerStatus) ? {
                loggerExchangeReason: undefined,
                requiredLoggerExchange: undefined,
            } : {}),
        }
    }, [checkIfToBeLoggerExchanged])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                {data?.id ? (
                    <Grid
                        container
                        spacing={3}
                    >
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <FormWithControls
                                name="ContainerStatusChangeDto"
                                value={value}
                                fields={fieldsWithClasses}
                                onSuccess={invalidate}
                                classNames={{
                                    gridWrapper: classes.gridWrapper,
                                    gridCardWrapper: classes.gridCardWrapper,
                                }}
                                setExternalValue={onChange}
                                exists
                                requestParams={containerRequests.statusChange({
                                    serialNumber,
                                })}
                                modifyDataBeforeSend={modifyDataBeforeSend}
                            />
                        </Grid>
                    </Grid>
                ) : null}
            </>
        </StatusHandler>
    )
}
